import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';

type BookStyle = 'fiction' | 'nonfiction' | 'academic' | 'children';
type Genre = 'mystery' | 'romance' | 'scifi' | 'fantasy' | 'thriller' | 'literary' | 'selfhelp' | 'business';

const fictionPrefixes = ['The Last', 'Whispers of', 'Beyond the', 'Echoes of', 'The Secret', 'Shadow of', 'The Lost', 'Dreams of'];
const nonfictionPrefixes = ['The Art of', 'Mastering', 'Understanding', 'The Power of', 'Essential', 'Complete Guide to', 'Principles of', 'Modern'];
const academicPrefixes = ['Advanced', 'Theoretical', 'Foundations of', 'Introduction to', 'Studies in', 'Analysis of', 'Research in', 'Contemporary'];
const childrenPrefixes = ['The Magic', 'Adventures of', 'Tales from', 'Journey to', 'The Wonderful', 'Little', 'My First', 'The Big'];

const fictionSuffixes = ['Kingdom', 'Mystery', 'Legacy', 'Chronicles', 'Prophecy', 'Secrets', 'Dawn', 'Dreams'];
const nonfictionSuffixes = ['Success', 'Leadership', 'Mindset', 'Strategy', 'Revolution', 'Mastery', 'Excellence', 'Transformation'];
const academicSuffixes = ['Theory', 'Methods', 'Principles', 'Studies', 'Research', 'Analysis', 'Practice', 'Applications'];
const childrenSuffixes = ['Adventure', 'Friends', 'Discovery', 'Journey', 'Stories', 'World', 'Magic', 'Treasures'];

const generateTitles = (style: BookStyle, genre: Genre, keywords: string): string[] => {
  let prefixes: string[];
  let suffixes: string[];

  switch (style) {
    case 'fiction':
      prefixes = fictionPrefixes;
      suffixes = fictionSuffixes;
      break;
    case 'nonfiction':
      prefixes = nonfictionPrefixes;
      suffixes = nonfictionSuffixes;
      break;
    case 'academic':
      prefixes = academicPrefixes;
      suffixes = academicSuffixes;
      break;
    case 'children':
      prefixes = childrenPrefixes;
      suffixes = childrenSuffixes;
      break;
  }

  const titles: string[] = [];
  for (let i = 0; i < 5; i++) {
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    const keyword = keywords ? ` ${keywords} ` : ' ';
    titles.push(`${prefix}${keyword}${suffix}`);
  }

  return titles;
};

const BookNameGenerator: React.FC = () => {
  const [style, setStyle] = useState<BookStyle>('fiction');
  const [genre, setGenre] = useState<Genre>('mystery');
  const [keywords, setKeywords] = useState('');
  const [generatedTitles, setGeneratedTitles] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);

  const handleGenerate = () => {
    const titles = generateTitles(style, genre, keywords);
    setGeneratedTitles(titles);
  };

  const toggleFavorite = (title: string) => {
    setFavorites(prev => 
      prev.includes(title) 
        ? prev.filter(n => n !== title)
        : [...prev, title]
    );
  };

  return (
    <section id="book-generator" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-purple-100 text-purple-800 px-4 py-1 rounded-full text-sm font-medium">
            AI-Powered Title Generation
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Book Title Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Book Type
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as BookStyle)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="fiction">Fiction</option>
                <option value="nonfiction">Non-Fiction</option>
                <option value="academic">Academic</option>
                <option value="children">Children's</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Genre
              </label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value as Genre)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="mystery">Mystery</option>
                <option value="romance">Romance</option>
                <option value="scifi">Science Fiction</option>
                <option value="fantasy">Fantasy</option>
                <option value="thriller">Thriller</option>
                <option value="literary">Literary Fiction</option>
                <option value="selfhelp">Self-Help</option>
                <option value="business">Business</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords (Optional)
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="E.g., Heart, Time, Journey"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
              />
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              className="bg-purple-600 text-white py-4 px-8 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Wand2 className="w-5 h-5" />
              Generate Titles
            </button>
          </div>

          {generatedTitles.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Generated Titles:
              </h3>
              <div className="grid grid-cols-1 gap-4 max-w-2xl mx-auto">
                {generatedTitles.map((title, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between"
                  >
                    <span className="text-lg">{title}</span>
                    <button
                      onClick={() => toggleFavorite(title)}
                      className={`ml-4 p-2 rounded-full transition-colors ${
                        favorites.includes(title)
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-gray-400 hover:text-gray-500'
                      }`}
                    >
                      ★
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: Click the star icon to save your favorite titles!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookNameGenerator;